import React from 'react';
import { motion } from 'framer-motion';
export interface PercentageBarProps {
  /** The percentage to display (0-100) */
  percentage: number;
  /** Optional label above the bar */
  label?: string;
  /** Optional second percentage for comparison */
  comparePercentage?: number;
  /** Labels for the percentages */
  percentageLabel?: string;
  compareLabel?: string;
  /** Show percentage text on the bar */
  showValue?: boolean;
  /** 'stimulus' = compact, 'support' = educational panel */
  mode?: 'stimulus' | 'support';
}
function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
function formatPercent(value: number): string {
  return new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0
  }).format(value);
}
function SingleBar({
  percentage,
  color,
  barLabel,
  showValue = true,
  delay = 0
}: {
  percentage: number;
  color: string;
  barLabel?: string;
  showValue?: boolean;
  delay?: number;
}) {
  const clamped = clamp(percentage, 0, 100);
  return <div className="space-y-1.5" data-id="element-13414" data-mp="opovan1fo0pf">
      {barLabel && <div className="text-xs font-medium text-slate-600" data-id="element-13415" data-mp="9w2h8nxcw1dp">{barLabel}</div>}
      <div className="relative h-8 bg-slate-100 rounded-lg overflow-hidden border border-slate-200" data-id="element-13416" data-mp="et2cs4wkxlqk">
        <motion.div initial={{
        width: 0
      }} animate={{
        width: `${clamped}%`
      }} transition={{
        delay,
        duration: 0.6,
        ease: 'easeOut'
      }} className={`absolute inset-y-0 left-0 rounded-lg ${color}`} data-id="element-13417" data-mp="12j6eu7u4ddfg" />
        {/* Grid lines at 25%, 50%, 75% */}
        {[25, 50, 75].map(mark => <div key={mark} className="absolute inset-y-0 w-px bg-slate-300/50" style={{
        left: `${mark}%`
      }} data-id="element-13418" data-mp="1lhirw4o5j7gb" />)}
        {/* Value label */}
        {showValue && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: delay + 0.4
      }} className="absolute inset-0 flex items-center justify-center text-sm font-bold" data-id="element-13419" data-mp="1v5wnsdipc6cx">
            <span className={clamped > 45 ? 'text-white' : 'text-slate-700'} data-id="element-13420" data-mp="1h6esrgetbwv4">
              {formatPercent(clamped)}%
            </span>
          </motion.div>}
      </div>
      {/* Scale markers */}
      <div className="flex justify-between text-[10px] text-slate-400 px-0.5" data-id="element-13421" data-mp="17hdf3wvqprkb">
        <span data-id="element-13422" data-mp="13fa233tfaewx">0%</span>
        <span data-id="element-13423" data-mp="13frzi3rtmz6f">25%</span>
        <span data-id="element-13424" data-mp="13gl531f1nimz">50%</span>
        <span data-id="element-13425" data-mp="13cxeaa0zhaby">75%</span>
        <span data-id="element-13426" data-mp="yg6v3dtvtymb">100%</span>
      </div>
    </div>;
}
export function PercentageBar({
  percentage,
  label,
  comparePercentage,
  percentageLabel,
  compareLabel,
  showValue = true,
  mode = 'support'
}: PercentageBarProps) {
  if (!isFiniteNumber(percentage)) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-13427" data-mp="3a30m6xlnie6b">
        <p className="text-red-600 text-sm font-medium" data-id="element-13428" data-mp="3a53ke4wyjdm4">
          Ongeldige percentage waarde
        </p>
      </div>;
  }
  const hasCompare = isFiniteNumber(comparePercentage);
  const clamped = clamp(percentage, 0, 100);
  // Stimulus mode: compact
  if (mode === 'stimulus') {
    return <div className="bg-blue-50 rounded-xl px-5 py-4 border border-blue-200" data-id="element-13429" data-mp="2mbbsbct6lhn9">
        {label && <p className="text-sm font-semibold text-blue-800 mb-3 text-center" data-id="element-13430" data-mp="2ra54n1wqf2lu">
            {label}
          </p>}
        <SingleBar percentage={clamped} color="bg-blue-500" barLabel={percentageLabel} showValue={showValue} data-id="element-13431" data-mp="304kwmz29vcyx" />
        {hasCompare && <div className="mt-3" data-id="element-13432" data-mp="27oikeslzre53">
            <SingleBar percentage={clamp(comparePercentage!, 0, 100)} color="bg-amber-500" barLabel={compareLabel} showValue={showValue} delay={0.2} data-id="element-13433" data-mp="27nqmhb4x94bn" />
          </div>}
      </div>;
  }
  // Support mode: educational panel
  const fractionText = (() => {
    if (clamped === 25) return '¼';
    if (clamped === 50) return '½';
    if (clamped === 75) return '¾';
    if (clamped === 10) return '¹⁄₁₀';
    if (clamped === 20) return '¹⁄₅';
    if (clamped === 33 || clamped === 33.3) return '⅓';
    if (clamped === 40) return '⅖';
    if (clamped === 60) return '⅗';
    if (clamped === 80) return '⅘';
    if (clamped === 66 || clamped === 66.6 || clamped === 66.7) return '⅔';
    return null;
  })();
  const decimalText = formatPercent(clamped / 100);
  return <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border-2 border-blue-200 shadow-sm" data-id="element-13434" data-mp="2c7eue05cqoxh">
      {label && <div className="mb-5 text-center" data-id="element-13435" data-mp="hw033ewp0hfm">
          <div className="inline-flex items-center gap-2 bg-white/90 rounded-xl px-4 py-2 border border-blue-200 shadow-sm" data-id="element-13436" data-mp="hx1gvr53fi11">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" data-id="element-13437" data-mp="2tl6a76ansxa1" />
            <span className="text-sm font-bold text-blue-900" data-id="element-13438" data-mp="2yh3qjclxa3mt">{label}</span>
          </div>
        </div>}

      <div className="space-y-4" data-id="element-13439" data-mp="2jqwfvj25powo">
        <SingleBar percentage={clamped} color="bg-blue-500" barLabel={percentageLabel} showValue={showValue} data-id="element-13440" data-mp="2ork6i3mh43qd" />

        {hasCompare && <SingleBar percentage={clamp(comparePercentage!, 0, 100)} color="bg-amber-500" barLabel={compareLabel} showValue={showValue} delay={0.2} data-id="element-13441" data-mp="2etxlr0ihthyh" />}

        {/* Educational explanation */}
        <div className="bg-white/80 rounded-xl p-4 border border-blue-100 shadow-sm" data-id="element-13442" data-mp="25thklxp82mws">
          <div className="flex items-start gap-3" data-id="element-13443" data-mp="3v8p5kp1kpgw6">
            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-13444" data-mp="41mio9srpwrr">
              <span className="text-indigo-600 text-sm" data-id="element-13445" data-mp="2kz6iaa6lcpz">📊</span>
            </div>
            <div data-id="element-13446" data-mp="3tra0jmgrxd7t">
              <h4 className="text-sm font-bold text-indigo-900 mb-1" data-id="element-13447" data-mp="3tqqscxgxe36z">
                Wat betekent dit?
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13448" data-mp="3osfkute3s4rx">
                <strong data-id="element-13449" data-mp="3jtm5evf9a3p4">{formatPercent(clamped)}%</strong> betekent{' '}
                <strong data-id="element-13450" data-mp="lb73sbr798y9">{formatPercent(clamped)}</strong> van de{' '}
                <strong data-id="element-13451" data-mp="laf70xlv956e">100</strong>.
                {fractionText && <span data-id="element-13452" data-mp="gd5e37z9qbdt">
                    {' '}
                    Als breuk is dat <strong data-id="element-13453" data-mp="bl3zvgijial5">{fractionText}</strong>.
                  </span>}
                <span data-id="element-13454" data-mp="6mkdxg3y4yss">
                  {' '}
                  Als kommagetal is dat <strong data-id="element-13455" data-mp="6k7yb9lnv0iw">{decimalText}</strong>.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}